import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Link2,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
} from "lucide-react";
import { toast } from "sonner";
import Seo, { absUrl } from "@/components/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import { Reveal } from "../components/landing/Reveal";
import { scrollToSection } from "../components/landing/scroll";
import { useSiteData } from "../context/SiteData";
import { useBooking } from "../context/BookingContext";
import { optImg } from "../lib/img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TourDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { tours, content, settings, fmtPrice } = useSiteData();
  const { openBooking } = useBooking();

  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    setTour(null);
    setActiveImg(0);
    axios
      .get(`${API}/tours/${slug}`)
      .then(({ data }) => alive && setTour(data))
      .catch(() => alive && setNotFound(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [slug]);

  useEffect(() => {
    const lenis = window.__lenis;
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [slug]);

  const backToTours = () => {
    navigate("/");
    setTimeout(() => scrollToSection("#tours"), 150);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="tour-detail-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--brand)]" />
      </div>
    );
  }

  if (notFound || !tour) {
    return (
      <div className="min-h-screen flex flex-col" data-testid="tour-not-found">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center gap-5 px-5 pt-32 pb-20 text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--brand)]">404</p>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#0b132b]">
            This tour doesn't exist (anymore)
          </h1>
          <p className="text-slate-600 max-w-md">
            It may have been unpublished or the link is wrong. Browse our current tours instead.
          </p>
          <button
            data-testid="tour-not-found-back-button"
            onClick={backToTours}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-6 py-3 shadow-lg shadow-[var(--brand)]/30 transition-colors hover:bg-[var(--brand-dark)]"
          >
            <ArrowLeft className="w-4 h-4" /> Browse all tours
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const gallery = [tour.image, ...(tour.images || [])].filter(Boolean);
  const pageUrl = window.location.href;
  const shareText = `${tour.name} — ${tour.destination}, Switzerland | Voyage Switzerland`;
  const whatsappNumber = (settings.whatsapp || "").replace(/[^0-9]/g, "");
  const related = tours.filter((t) => t.id !== tour.id).slice(0, 3);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      toast.success("Link copied — share it anywhere!");
    } catch {
      toast.error("Couldn't copy the link");
    }
  };

  const shareWhatsApp = () =>
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${pageUrl}`)}`, "_blank");

  const shareEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(
      `Check out this tour:\n\n${tour.name} (${tour.duration}) — ${tour.destination}, Switzerland\n${pageUrl}`
    )}`;
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TouristTrip",
        name: tour.name,
        description: tour.description,
        image: gallery.map(absUrl),
        offers: {
          "@type": "Offer",
          price: tour.sale_price || tour.price,
          priceCurrency: settings.currency,
          availability: "https://schema.org/InStock",
          url: `${window.location.origin}/tours/${tour.slug || tour.id}`,
        },
        ...(tour.itinerary?.length
          ? {
              itinerary: {
                "@type": "ItemList",
                itemListElement: tour.itinerary.map((step, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  name: step,
                })),
              },
            }
          : {}),
      },
      ...(tour.faqs?.length
        ? [
            {
              "@type": "FAQPage",
              mainEntity: tour.faqs.map((f) => ({
                "@type": "Question",
                name: f.question,
                acceptedAnswer: { "@type": "Answer", text: f.answer },
              })),
            },
          ]
        : []),
    ],
  };

  return (
    <div data-testid="tour-detail-page">
      <Seo
        title={`${tour.name} — ${tour.destination} Tour | Voyage Switzerland`}
        siteName="Voyage Switzerland"
        description={`${tour.description} ${tour.duration} guided tour in ${tour.destination}, Switzerland — from ${fmtPrice(
          tour.sale_price || tour.price
        )} per person.`}
        image={tour.image}
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-[110px] sm:pt-[130px] pb-10 sm:pb-14 bg-[#f8fafc] overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <button
            data-testid="tour-detail-back-button"
            onClick={backToTours}
            className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-[var(--brand)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All tours
          </button>
          <div className="mt-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <Reveal>
              <p
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[var(--brand)]"
                data-testid="tour-detail-destination"
              >
                <MapPin className="w-3.5 h-3.5" /> {tour.destination}, Switzerland
              </p>
              <h1
                className="mt-3 font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#0b132b] leading-[1.05]"
                data-testid="tour-detail-title"
              >
                {tour.name}
              </h1>
              <p
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-sm font-bold text-[#0b132b]"
                data-testid="tour-detail-duration"
              >
                <Clock className="w-4 h-4 text-[var(--brand)]" /> {tour.duration}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="lg:text-right" data-testid="tour-detail-price">
                <span className="text-[11px] font-semibold text-slate-400 block mb-1">from</span>
                {tour.sale_price ? (
                  <>
                    <span className="font-display font-extrabold text-4xl text-[var(--brand)]">
                      {fmtPrice(tour.sale_price)}
                    </span>
                    <span className="ml-2 text-lg font-semibold text-slate-400 line-through">
                      {fmtPrice(tour.price)}
                    </span>
                  </>
                ) : (
                  <span className="font-display font-extrabold text-4xl text-[var(--brand)]">
                    {fmtPrice(tour.price)}
                  </span>
                )}
                
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="pb-24 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_360px] gap-10 items-start">
          {/* Main column */}
          <div className="min-w-0">
            {/* Gallery */}
            <Reveal>
              <div
                className="rounded-3xl overflow-hidden shadow-[0_24px_60px_-24px_rgba(11,19,43,0.25)]"
                data-testid="tour-detail-gallery"
              >
                <motion.img
                  key={activeImg}
                  src={optImg(gallery[activeImg], 1600)}
                  alt={`${tour.name} — ${tour.destination}, Switzerland — photo ${activeImg + 1}`}
                  initial={{ opacity: 0.4, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="w-full h-[300px] sm:h-[440px] object-cover"
                />
              </div>
              {gallery.length > 1 && (
                <div className="mt-4 flex gap-3 overflow-x-auto pb-1" data-testid="tour-detail-thumbs">
                  {gallery.map((src, i) => (
                    <button
                      key={i}
                      data-testid={`tour-detail-thumb-${i}`}
                      onClick={() => setActiveImg(i)}
                      className={`shrink-0 w-24 h-16 rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                        i === activeImg
                          ? "border-[var(--brand)] shadow-md"
                          : "border-transparent opacity-60 hover:opacity-100"
                      }`}
                      aria-label={`Show photo ${i + 1}`}
                    >
                      <img src={optImg(src, 300)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </Reveal>

            {/* Description */}
            <Reveal className="mt-12">
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0b132b]" data-testid="tour-detail-about-title">
                About this tour
              </h2>
              <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed" data-testid="tour-detail-description">
                {tour.description}
              </p>
            </Reveal>

            {/* Itinerary */}
            {tour.itinerary?.length > 0 && (
              <Reveal className="mt-12">
                <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0b132b]" data-testid="tour-detail-itinerary-title">
                  Your itinerary
                </h2>
                <ol className="mt-6 relative border-l-2 border-[var(--brand)]/20 ml-3 space-y-6" data-testid="tour-detail-itinerary">
                  {tour.itinerary.map((step, i) => (
                    <li key={i} className="relative pl-8" data-testid={`tour-detail-itinerary-step-${i}`}>
                      <span className="absolute -left-[17px] top-0 w-8 h-8 rounded-full bg-[var(--brand)] text-white text-xs font-extrabold flex items-center justify-center shadow-lg shadow-[var(--brand)]/30">
                        {i + 1}
                      </span>
                      <p className="text-base text-slate-700 leading-relaxed pt-1">{step}</p>
                    </li>
                  ))}
                </ol>
              </Reveal>
            )}
            {/* FAQs */}
            {tour.faqs?.length > 0 && (
              <Reveal className="mt-12">
                <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0b132b]" data-testid="tour-detail-faq-title">
                  Good to know
                </h2>
                <div className="mt-5 rounded-3xl bg-white border border-slate-100 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)] px-6" data-testid="tour-detail-faqs">
                  <Accordion type="single" collapsible>
                    {tour.faqs.map((faq, i) => (
                      <AccordionItem key={i} value={`faq-${i}`} className="border-slate-100" data-testid={`tour-detail-faq-${i}`}>
                        <AccordionTrigger
                          className="text-base font-bold text-[#0b132b] hover:text-[var(--brand)] hover:no-underline"
                          data-testid={`tour-detail-faq-question-${i}`}
                        >
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent
                          className="text-slate-600 leading-relaxed"
                          data-testid={`tour-detail-faq-answer-${i}`}
                        >
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </Reveal>
            )}
          </div>
          <Reveal delay={0.1} className="lg:sticky lg:top-28">
            <div
              className="rounded-3xl bg-white border border-slate-100 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)] p-6"
              data-testid="tour-detail-booking-card"
            >
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Reserve your seat</p>
              <p className="mt-2 font-display font-bold text-xl text-[#0b132b] leading-tight">{tour.name}</p>
              <p className="mt-1 text-sm text-slate-500">
                {tour.duration} · {tour.destination}
              </p>
              <button
                data-testid="tour-detail-book-button"
                onClick={() => openBooking({ mode: "tour", tour })}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-6 py-3.5 shadow-lg shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] hover:-translate-y-0.5"
              >
                Book This Tour <ArrowRight className="w-4 h-4" />
              </button>

              {/* Share */}
              <div className="mt-6 pt-5 border-t border-slate-100">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Share this tour</p>
                <div className="mt-3 flex gap-2">
                  <button
                    data-testid="share-copy-button"
                    onClick={copyLink}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2.5 text-xs font-bold text-slate-600 transition-colors hover:border-[var(--brand)] hover:text-[var(--brand)]"
                  >
                    <Link2 className="w-4 h-4" /> Copy link
                  </button>
                  <button
                    data-testid="share-whatsapp-button"
                    onClick={shareWhatsApp}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2.5 text-xs font-bold text-slate-600 transition-colors hover:border-[#25D366] hover:text-[#25D366]"
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </button>
                  <button
                    data-testid="share-email-button"
                    onClick={shareEmail}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2.5 text-xs font-bold text-slate-600 transition-colors hover:border-[var(--brand)] hover:text-[var(--brand)]"
                  >
                    <Mail className="w-4 h-4" /> Email
                  </button>
                </div>
              </div>

              {/* Contact */}
              <div className="mt-6 pt-5 border-t border-slate-100" data-testid="tour-detail-contact-card">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                  Questions about this tour?
                </p>
                <div className="mt-3 flex flex-col gap-2">
                  <a
                    data-testid="contact-call-button"
                    href={`tel:${content.contact_phone}`}
                    className="inline-flex items-center gap-3 rounded-xl bg-[var(--brand-soft)] px-4 py-3 text-sm font-bold text-[var(--brand)] transition-colors hover:bg-[var(--brand)] hover:text-white"
                  >
                    <Phone className="w-4 h-4" /> Call {content.contact_phone}
                  </a>
                  {whatsappNumber && (
                    <a
                      data-testid="contact-whatsapp-button"
                      href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
                        `Hi! I'm interested in the "${tour.name}" tour (${tour.duration}, ${tour.destination}).`
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-3 rounded-xl bg-[#25D366]/10 px-4 py-3 text-sm font-bold text-[#1da851] transition-colors hover:bg-[#25D366] hover:text-white"
                    >
                      <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
                    </a>
                  )}
                  <a
                    data-testid="contact-email-button"
                    href={`mailto:${content.contact_email}?subject=${encodeURIComponent(
                      `Enquiry: ${tour.name} tour`
                    )}`}
                    className="inline-flex items-center gap-3 rounded-xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 transition-colors hover:bg-[#0b132b] hover:text-white"
                  >
                    <Mail className="w-4 h-4" /> Email {content.contact_email}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Related tours */}
      {related.length > 0 && (
        <section className="py-20 bg-white border-t border-slate-100" data-testid="related-tours-section">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <Reveal>
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0b132b]">
                More tours you'll love
              </h2>
            </Reveal>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((t, i) => (
                <Reveal key={t.id} delay={i * 0.08}>
                  <Link
                    to={`/tours/${t.slug || t.id}`}
                    data-testid={`related-tour-card-${t.id}`}
                    className="group block rounded-3xl bg-white border border-slate-100 overflow-hidden shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)] hover:shadow-[0_30px_60px_-24px_rgba(225,29,72,0.35)] hover:border-[var(--brand)]/25 transition-all duration-300 hover:-translate-y-1.5"
                  >
                    <div className="h-44 overflow-hidden">
                      <img
                        src={optImg(t.image, 800)}
                        alt={`${t.name} — ${t.destination}, Switzerland`}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-5">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t.destination}</p>
                      <h3 className="mt-1 font-display font-bold text-lg text-[#0b132b] leading-tight group-hover:text-[var(--brand)] transition-colors">
                        {t.name}
                      </h3>
                      <p className="mt-2 text-sm font-bold text-[var(--brand)]">
                        from {fmtPrice(t.sale_price || t.price)}
                      </p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};
